/**
 * VTP Response Parser
 *
 * Parses the VTP API response and extracts all VTP-specific data including
 * vehicle details, prices, and dealer photos.
 */

import type { IVtpDetails, IVtpPhoto } from '../types';
import type { IVtpResponse, IVtpItem, IVtpItemValue, IVtpCarDetailResponse } from './vtp.types';
import { VTP_ITEM_KEYS, VTP_PRICE_KEYS } from './vtp.types';

/**
 * Result of parsing a VTP response
 */
export interface IVtpParsedResult extends IVtpEnergyLabel {
  /**
   * Absent only on corrupt VTP records — every genuine stock car has one. Without it VSRS is
   * unreachable (its lookup key is the VIN) and the vehicle can only be served as partial data.
   */
  vin?: string;
  vtpDetails: IVtpDetails;
  vtpPhotos: IVtpPhoto[];
  /** Link to the per-car detail resource, which carries the EEC label fields. */
  detailHref?: string;
  /**
   * True when the code resolved from VTP's used-stock list rather than its new-car list.
   *
   * VTP is the only source that distinguishes the two; a VIN lookup or an OKAPI/CASA marketing code
   * carries no such marker, so those vehicles are left unclassified.
   */
  isUsedCar?: boolean;
}

/**
 * Energy label data available only from the VTP per-car detail resource.
 */
export interface IVtpEnergyLabel {
  /** Ready-made label URL including the CO2 segment, e.g. .../eec-label/fr/fr/E/166 */
  energyLabelUrl?: string;
  /** Efficiency class on its own, e.g. 'E' */
  efficiencyClass?: string;
}

/**
 * Finds an item by key in the VTP items array
 */
const findItem = (items: IVtpItem[], key: string): IVtpItem | undefined => items.find((item) => item?.key === key);

/**
 * Finds a price value by key in the prices item
 */
const findPriceValue = (pricesItem: IVtpItem | undefined, priceKey: string): IVtpItemValue | undefined =>
  pricesItem?.values?.find((v) => v.key === priceKey);

/**
 * Extracts dealer photos from the VTP response using functional array methods
 */
const extractDealerPhotos = (car: IVtpResponse['cars'][0]['car']): IVtpPhoto[] => {
  const dealerImagesGroup = car.images?.find((img) => img.key === 'dealerImages');

  return (
    dealerImagesGroup?.imageGroup?.images
      ?.filter((imageEntry) => imageEntry.image?.href)
      .map((imageEntry) => ({
        url: imageEntry.image.href,
        isRealPhoto: true, // All dealer images are real photos
        width: imageEntry.image.width,
        height: imageEntry.image.height,
      })) ?? []
  );
};

/**
 * Formats mileage with unit if available
 */
const formatMileage = (mileageItem: IVtpItem | undefined): string | undefined => {
  if (!mileageItem?.value) return undefined;
  return mileageItem.unit ? `${mileageItem.value} ${mileageItem.unit}` : mileageItem.value;
};

/**
 * Extracts VTP details from the items array using immutable object construction
 * Only extracts fields specified in AC2: price, firstRegistration, mileage, commissionNumber
 */
const extractVtpDetails = (items: IVtpItem[]): IVtpDetails => {
  const pricesItem = findItem(items, VTP_ITEM_KEYS.PRICES);
  const firstRegistration = findItem(items, VTP_ITEM_KEYS.INITIAL_REG)?.value;
  const mileage = formatMileage(findItem(items, VTP_ITEM_KEYS.MILEAGE));
  const commissionNumber = findItem(items, VTP_ITEM_KEYS.KNR)?.value;
  const price = findPriceValue(pricesItem, VTP_PRICE_KEYS.SALE)?.formattedValue;

  // Build object immutably with only defined properties
  return {
    ...(firstRegistration && { firstRegistration }),
    ...(mileage && { mileage }),
    ...(price && { price }),
    ...(commissionNumber && { commissionNumber }),
  };
};

/**
 * Parses the full VTP API response and extracts all relevant data
 *
 * @param response - The VTP API response
 * @returns Parsed VTP data including VIN, details, and photos. A missing VIN is reported as
 *   `vin: undefined` rather than thrown: the record is corrupt but the details and photos it does
 *   carry are still servable, and the caller decides whether to degrade or fail.
 * @throws Error if no cars found
 */
export const parseVtpResponse = (response: IVtpResponse): IVtpParsedResult => {
  const [firstCar] = response.cars ?? [];

  if (!firstCar) {
    throw new Error('No cars found in VTP response');
  }

  const { car } = firstCar;
  const { items } = car;

  const vin = findItem(items, VTP_ITEM_KEYS.VIN)?.value;

  return {
    ...(vin && { vin }),
    vtpDetails: extractVtpDetails(items),
    vtpPhotos: extractDealerPhotos(car),
    ...(firstCar.href && { detailHref: firstCar.href }),
  };
};

/**
 * Extracts the EEC label data from a VTP per-car detail response.
 *
 * Both fields are optional by nature: verified live, only new cars (nwb) carry them — used cars
 * (gwb) have neither, and no search response has them at all. An empty result is normal, not an
 * error, and callers fall back to the VSRS-derived class.
 */
export const parseVtpEnergyLabel = (detail: IVtpCarDetailResponse | undefined): IVtpEnergyLabel => {
  const items = detail?.items;
  if (!Array.isArray(items)) return {};

  const energyLabelUrl = findItem(items, VTP_ITEM_KEYS.EEC_LABEL)?.value;
  const efficiencyClass = findItem(items, VTP_ITEM_KEYS.ENERGY_EFFICIENCY)?.value;

  return {
    ...(energyLabelUrl && { energyLabelUrl }),
    ...(efficiencyClass && { efficiencyClass }),
  };
};

/**
 * Converts VTP photos to car image elements for gallery display
 *
 * @param vtpPhotos - Array of VTP photos from the parser
 * @returns Array of car image elements with imageType: 'real'
 */
export const vtpPhotosToCarImageElements = (
  vtpPhotos: IVtpPhoto[]
): Array<{ type: 'image'; title: string; source: string; imageType: 'real' }> =>
  vtpPhotos.map((photo, index) => ({
    type: 'image' as const,
    title: `Dealer Photo ${index + 1}`,
    source: photo.url,
    imageType: 'real' as const,
  }));
